import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { isScreenshotNotFound, type MigrationLogScreenshot } from "@/api/config-migration";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useRunLogScreenshot } from "@/hooks/useConfigMigration";
import { useObjectUrl } from "@/hooks/useObjectUrl";
import { telemetry } from "@/lib/telemetry";

/**
 * Modal do print de tela de uma linha do log do cadastro (spec evidencia-de-tela, §8).
 *
 * Recebe só o que exibe — `logId` já como número, o print e os textos —, não a
 * linha inteira: quem monta o `ScreenshotItem` (o reducer da conversa e a fonte do
 * terminal) é quem garante que a linha tem `id` e print.
 *
 * A imagem vem da mesma consulta da miniatura (`runLogScreenshotQuery`): em geral já
 * está no cache quando o modal abre, e fechá-lo não cancela nada, porque o observador
 * da miniatura continua vivo. Só quando a imagem ainda não baixou é a montagem do modal
 * que a pede. A imagem é exibida, nunca salva — não há botão de download e o servidor
 * responde `inline`.
 */
export function ScreenshotDialog({
  projectId,
  runId,
  logId,
  screenshot,
  message,
  sapMessage,
  createdAt,
  title: titleOverride,
  onClose,
}: {
  projectId: string;
  runId: string;
  logId: number;
  screenshot: MigrationLogScreenshot;
  message: string;
  sapMessage?: string;
  createdAt: string;
  /** Título do modal. Sem ele, o do Nó 02, pelo tipo do print. */
  title?: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [actualSize, setActualSize] = useState(false);
  const query = useRunLogScreenshot(projectId, runId, logId, true);
  const imageUrl = useObjectUrl(query.data);
  // O erro de imagem é guardado junto com a URL que falhou, não como booleano: o
  // "Try again" refaz a busca e chega bytes novos, com outra URL — um booleano
  // preso por um `onError` tardio da URL velha esconderia a imagem boa.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const imageFailed = imageUrl !== null && failedUrl === imageUrl;

  const title =
    titleOverride ??
    (screenshot.kind === "error" ? t("SAP screen error") : t("Final screen of the unit"));
  const description = sapMessage ?? message;
  const notFound = query.isError && isScreenshotNotFound(query.error);
  // O botão só existe em linha que diz ter print, então 404 aqui é anomalia (objeto
  // sumido, prefixo errado, api e agents em versões diferentes). Num efeito, e não no
  // render, para o aviso sair uma vez por abertura mesmo com novas renderizações.
  useEffect(() => {
    if (!notFound) return;
    telemetry.captureWarning("screenshot", "screenshot not found for a log line that claims one", {
      runId,
      logId,
    });
  }, [notFound, runId, logId]);
  // Enquanto a busca está em voo (primeira vez ou depois do "Try again") vale o
  // esqueleto: nem a imagem velha, que remontaria com a mesma URL quebrada e
  // falharia de novo na hora, nem o bloco de erro e o botão, que deixariam um
  // segundo clique reiniciar a busca.
  const showError = !query.isFetching && (query.isError || imageFailed);
  const showImage = !query.isFetching && !showError && imageUrl !== null;
  const retry = () => {
    setFailedUrl(null);
    void query.refetch();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        data-testid="screenshot-dialog"
        className="grid max-h-[90vh] w-[95vw] max-w-[95vw] grid-rows-[auto_1fr] gap-3"
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {description}
            {createdAt && (
              <span className="ml-2 text-xs">{new Date(createdAt).toLocaleString()}</span>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 overflow-auto rounded-md border bg-muted/30">
          {showError ? (
            <div className="flex flex-col items-center gap-3 p-8 text-sm">
              <p>{notFound ? t("Screenshot not available") : t("Could not load the screenshot.")}</p>
              {!notFound && (
                <Button variant="outline" size="sm" onClick={retry}>
                  {t("Try again")}
                </Button>
              )}
            </div>
          ) : showImage ? (
            <button
              type="button"
              aria-pressed={actualSize}
              aria-label={
                actualSize ? t("Click to fit to the screen") : t("Click to view at actual size")
              }
              onClick={() => setActualSize((value) => !value)}
              className={actualSize ? "block cursor-zoom-out" : "block w-full cursor-zoom-in"}
            >
              <img
                src={imageUrl}
                alt={description}
                decoding="async"
                onError={() => {
                  setFailedUrl(imageUrl);
                  // O download deu certo e os bytes não viram imagem: tamanho e tipo
                  // distinguem resposta truncada de página de erro no lugar do PNG.
                  telemetry.captureWarning("screenshot", "screenshot image failed to decode", {
                    runId,
                    logId,
                    size: query.data?.size,
                    type: query.data?.type,
                  });
                }}
                className={
                  actualSize ? "max-w-none" : "mx-auto max-h-[75vh] w-auto max-w-full object-contain"
                }
              />
            </button>
          ) : (
            <div role="status" className="aspect-video w-full animate-pulse bg-muted">
              <span className="sr-only">{t("Loading screenshot…")}</span>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
